import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/shared/components/ui/button/Button';
import { Card, CardHeader, CardTitle } from '@/shared/components/ui/card/Card';
import { Table } from '@/shared/components/ui/table/Table';
import { DOCUMENT_TYPE_LABELS } from '../../utils/documentTypeLabels';
import type { TemplateAssignment } from '../../types/templateAssignments.types';
import { AssignedTemplateLabel } from './AssignedTemplateLabel';
import { AssignmentActions } from './AssignmentActions';
import { DealerLabel } from './DealerLabel';
import type { ProductDocumentsLayoutProps } from './productDocumentsView.types';

type Props = Pick<
  ProductDocumentsLayoutProps,
  'dealerAssignments' | 'dealerNames' | 'isResolvingDealers' | 'canAdd' | 'canEdit' | 'canDelete' | 'onAddOverride' | 'onChange' | 'onUnassign'
>;

export function DealerAssignmentsTable({ dealerAssignments, dealerNames, isResolvingDealers = false, canAdd, canEdit, canDelete, onAddOverride, onChange, onUnassign }: Props) {
  const columns = useMemo<ColumnDef<TemplateAssignment>[]>(
    () => [
      {
        id: 'dealer',
        header: 'Dealer',
        cell: ({ row }) => <DealerLabel dealerId={row.original.dealerId ?? ''} dealerNames={dealerNames} isResolvingDealers={isResolvingDealers} />,
      },
      { id: 'type', header: 'Document', cell: ({ row }) => DOCUMENT_TYPE_LABELS[row.original.documentType] },
      {
        id: 'template',
        header: 'Template',
        cell: ({ row }) => <AssignedTemplateLabel template={row.original.template} dealerNames={dealerNames} isResolvingDealers={isResolvingDealers} />,
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <AssignmentActions
            label={`${DOCUMENT_TYPE_LABELS[row.original.documentType]} for ${dealerNames.get(row.original.dealerId ?? '') ?? 'dealer'}`}
            canAdd={canAdd}
            canEdit={canEdit}
            canDelete={canDelete}
            onChange={() => onChange(row.original)}
            onUnassign={() => onUnassign(row.original)}
          />
        ),
      },
    ],
    [dealerNames, isResolvingDealers, canAdd, canEdit, canDelete, onChange, onUnassign],
  );

  return (
    <Card ariaLabelledBy="dealer-overrides-title">
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle id="dealer-overrides-title">Dealer overrides</CardTitle>
          <p className="mt-1 text-sm text-mp-muted">
            Used instead of the product template for that dealer's agreements.
          </p>
        </div>
        {canAdd && (
          <Button type="button" size="sm" variant="primary" appearance="outline" onClick={onAddOverride}>
            Add dealer override
          </Button>
        )}
      </CardHeader>
      <Table<TemplateAssignment>
        data={[...dealerAssignments]}
        columns={columns}
        getRowId={(row) => row.id}
        emptyMessage="No dealer overrides. Every dealer receives the product templates."
        framed={false}
      />
    </Card>
  );
}
